import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { ArgumentException } from '../../exceptions.js';
import { Currency } from '../../models/serializable/currency.js';
import { OnlineEventPassOrder } from '../../models/serializable/online-event-pass-order.js';
import { Price } from '../../models/serializable/price.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { LegacyOrderDiscountBreakdown } from './discount-breakdown.js';
import { LegacyOrderFeeBreakdown } from './fee-breakdown.js';
import { BasicOrderPriceBreakdown, IOrderPriceBreakdown } from './order-price-breakdown.js';
import { IPaymentHelperAdapter } from './payment-helper-adapter.js';

export class EventPassOrderPaymentHelper extends IPaymentHelperAdapter {
  public readonly orderTypeId: string = OnlineEventPassOrder.TYPE_ID;

  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof EventPassOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is EventPassOrderPaymentHelper {
    return dm instanceof EventPassOrderPaymentHelper;
  }

  getCurrencyOfOrder(order: CustomerOrderBase): Currency | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return order.passes.length > 0 ? order.passes[0].price.currency : null;
  }

  calculateDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceBreakdownForOnlineOrder(order: CustomerOrderBase): IOrderPriceBreakdown | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const itemPrice = this.calculatePriceForOnlineEventPassOrder({ order, applyFee: false });
    if(itemPrice == null) {
      return null;
    }

    const standardFeePrice = order.applicationFee?.clone() ?? null;
    const totalFeePrice = order.applicationFee?.clone() ?? null;
    const totalPrice = totalFeePrice != null ? itemPrice.add(totalFeePrice) : itemPrice.clone();

    return BasicOrderPriceBreakdown.pass({
      donationPrice: null,
      itemPrice,
      standardFeePrice,
      totalFeePrice,
      totalPrice,
    });
  }

  calculateDiscountBreakdownForOrder(order: CustomerOrderBase): LegacyOrderDiscountBreakdown | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateFeeBreakdownForOrder(order: CustomerOrderBase): LegacyOrderFeeBreakdown | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineEventPassOrder({ order, applyFee: true });
  }

  calculateCheckoutPriceForDiscountProvider(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineEventPassOrder({ order, applyFee: false });
  }

  calculateIncomeForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineEventPassOrder({ order, applyFee: false });
  }

  calculateIncomeWithoutDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineEventPassOrder({ order, applyFee: false });
  }

  canCreateRefundForOnlineOrder(order: CustomerOrderBase): boolean | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateExistingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateRemainingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateMaxRefundForOnlineOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventPassOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  private calculatePriceForOnlineEventPassOrder({ order, applyFee }: {order: OnlineEventPassOrder, applyFee: boolean}): Price | null {
    const currency = this.getCurrencyOfOrder(order);
    if(isNullOrUndefined(currency)) {
      return null;
    }

    let orderPrice = Price.zero(currency);

    for(const item of order.passes) {
      if(item.price.currency != currency) {
        return null;
      }

      orderPrice = orderPrice.add(item.price);
    }

    if(applyFee && isNotNullOrUndefined(order.applicationFee)) {
      orderPrice = orderPrice.add(order.applicationFee);
    }

    return orderPrice;
  }
}
